import { theme } from "@freshpaint/mui-theme";
import "@freshpaint/mui-theme/src/theme/theme.css";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { ThemeProvider } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import { GithubAuthProvider } from "firebase/auth";
import clsx from "clsx";
import _ from "lodash";
import queryString from "query-string";
import { Component, lazy, Suspense, useEffect, useState } from "react";
import { withAlert, type AlertManager } from "react-alert";
import toast from "react-hot-toast";
import { QueryClientProvider } from "react-query";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";

import { EyeOffIcon } from "lucide-react";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter5Adapter } from "use-query-params/adapters/react-router-5";

import { BacknavLocationsProvider } from "@/design-system/components/Backnav/context";
import { Commandbar } from "@/design-system/components/Commandbar/Commandbar";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
// --- Eager imports: auth flow, hooks, utilities, route-config constants ---
import { RequiredAccountInfoCollectionModal } from "@/account/components/RequiredAccountInfoCollectionModal/RequiredAccountInfoCollectionModal";
import {
  getCurrentUser,
  initializeFirebase,
  onAuthStateChanged,
  AuthUser,
} from "@/auth/firebase";
import AcceptInvitePage from "./AcceptInvitePage/AcceptInvitePage";
import { PendingInvitePage } from "./AcceptInvitePage/PendingInvitePage";
import usePermissions from "./access-control/queries/usePermissions";
import {
  fetchAccountMetadata,
  setCurrentEnvironment,
} from "./actions/environments";
import { fetchFunctionDefinitions } from "./actions/functions";
import { fetchSources } from "./actions/sources";
import { fetchUsers } from "./actions/users";
import useCanViewDestinationRanks from "./admin/useCanViewDestinationRanks";
import EntryPage from "./App/EntryPage";
import AppNoEnvironments from "./AppNoEnvironments";
import { allowAuditSelfServe, AUDIT_LOGS_PATH } from "./AuditLogs";
import { ALLOWLIST_PAGE_BASE_PATH } from "./blackhole/allowlist/common";
import { FreshChat } from "./components/FreshChat";
import FreshpaintUser from "./components/users/FreshpaintUser";
import { inDev, inStaging } from "./config";
import { consentManagementIntegrations } from "./consent-management/consentManagement";
import DelightedNPS from "./DelightedNPS";
import { SaaSDestination } from "./destinations/destinations";
import { ErrorPage } from "./ErrorPage";
import useOnEnvironmentSet from "./hooks/on-environment-set";
import useCurrentEnvironment from "./hooks/use-environment";
import useAccountMetadata from "./hooks/useAccountMetadata";
import { useAllSaasDestinations } from "./hooks/useAllSaasDestinations";
import useCanAccessCRO from "./hooks/useCanAccessCRO";
import useFeatureFlags from "./hooks/useFeatureFlag";
import { useScriptTag } from "./hooks/useScriptTag";
import useScrollToTopOnNavigationChange from "./hooks/useScrollToTopOnNavigationChange";
import useUsers from "./hooks/useUsers";
import { AuthenticatedLayout } from "./NavBar/AuthenticatedLayout";
import { signout } from "./NavBar/signout";
import { DestinationOAuth } from "./oauth";
import PageWithTitle from "./PageWithTitle";
import { queryClient } from "./queryClient";
import replaceHistoryStateToSamePageWithoutQueryParams from "./replaceHistoryStateToSamePageWithoutQueryParams";
import SessionTimeoutManager from "./sessionTimeout/SessionTimeoutManager";
import SignInScreen from "./SignInScreen";
import SignoutPage from "./SignoutPage";
import SurveyStoreProvider from "./surveys/SurveyStoreProvider";
import { ThemeProvider as DarkModeProvider } from "./theme/ThemeContext";
import useWTMURLs from "./trackers/useWTMURLs";
import { WebTrackerDetailsPageRedirect } from "./trackers/WebTrackerDetailsPageRedirect";
import { WTMTab } from "./trackers/wtmConstants";
import {
  CustomDateFnsUtils,
  isAccountInHIPAAMode,
  isAccountInHIPAAModeV2,
  makeRequest,
  MASQUERADE_LOCAL_STORAGE_KEY,
  userIsFPEmployee,
} from "./util";
import useCanAccessAiChat from "@/hooks/useCanAccessAiChat";
import { MoneySavedPage } from "./MoneySavedPage";

// --- Lazy-loaded page components for code splitting ---

// Community
const CommunityPage = lazy(() => import("./auth/CommunityPage"));

// Forms (pulls in @formio ~800KB)
const FormBuilderPage = lazy(() => import("./forms/FormBuilderPage"));
const FormListPage = lazy(() => import("./forms/FormListPage"));
const FormSubmissionListPage = lazy(
  () => import("./forms/FormSubmissionListPage"),
);
const FormSubmissionView = lazy(() => import("./forms/FormSubmissionView"));
const FormSubscriptionList = lazy(() => import("./forms/FormSubscriptionList"));

// Analytics & Insights (pulls in highcharts ~400KB)
const AnalyticsDashboardPage = lazy(() => import("./analytics/DashboardPage"));
const CampaignsListPage = lazy(() => import("./analytics/CampaignsListPage"));
const ServiceLinesListPage = lazy(
  () => import("./analytics/ServiceLinesListPage"),
);
const InsightsPage = lazy(() =>
  import("./InsightsPage/InsightsPage").then((m) => ({
    default: m.InsightsPage,
  })),
);
const MarketingPerformanceAnalysisPage = lazy(
  () =>
    import("./MarketingPerformanceAnalysis/MarketingPerformanceAnalysisPage"),
);
const MarketingPerformanceAnalysisDetailPage = lazy(
  () =>
    import(
      "./MarketingPerformanceAnalysis/MarketingPerformanceAnalysisDetailPage"
    ),
);

// Consent management
const ConsentMetricsPage = lazy(() =>
  import(
    "./consent-management/components/ConsentMetrics/ConsentMetricsPage"
  ).then((m) => ({
    default: m.ConsentMetricsPage,
  })),
);

// Destinations
const DestinationConfigPage = lazy(
  () => import("./destinations/DestinationConfigPage"),
);
const DestinationEventTester = lazy(
  () => import("./destinations/DestinationEventTester"),
);
const DestinationDeliveryMetrics = lazy(
  () => import("./destinations/DestinationMetrics"),
);
const DestinationPHIAllowListPage = lazy(
  () => import("./destinations/DestinationPHIAllowListPage"),
);
const DestinationTraceListViewerWrapper = lazy(() =>
  import("./destinations/Traces").then((m) => ({
    default: m.DestinationTraceListViewerWrapper,
  })),
);
const DestinationTraceViewerWrapper = lazy(() =>
  import("./destinations/Traces").then((m) => ({
    default: m.DestinationTraceViewerWrapper,
  })),
);
const CatalogPage = lazy(() => import("./destinations/v2/CatalogPage"));
const DashboardPage = lazy(() => import("./destinations/v2/DashboardPage"));
const DestinationsListPage = lazy(() => import("./DestinationsListPage"));
const WarehousesPage = lazy(() => import("./DashboardPage"));

// System Status
const SystemStatusPage = lazy(() => import("./SystemStatus/SystemStatusPage"));

// Admin pages (employee-only, pulls in highcharts)
const AccountPage = lazy(() => import("./admin/AccountPage"));
const AccountMetricsRedirectPage = lazy(
  () => import("./admin/AccountPage/AccountMetricsRedirectPage"),
);
const AccountsPage = lazy(() => import("./admin/AccountsPage"));
const DestinationPage = lazy(() => import("./admin/DestinationPage"));
const DestinationRanksPage = lazy(() => import("./admin/DestinationRanksPage"));
const EnvironmentPage = lazy(() => import("./admin/EnvironmentPage"));
const EnvironmentMetricsRedirectPage = lazy(
  () => import("./admin/EnvironmentPage/EnvironmentMetricsRedirectPage"),
);
const EventPage = lazy(() => import("./admin/EventPage"));
const AdPerformancePage = lazy(
  () => import("./admin/AdPerformancePage/AdPerformancePage"),
);
const MtuReports = lazy(() =>
  import("./mtureports/MtuReports").then((m) => ({
    default: m.MtuReports,
  })),
);
const Entitlements = lazy(() =>
  import("./entitlements").then((m) => ({ default: m.Entitlements })),
);
const GtmMigration = lazy(() =>
  import("./gtmmigration/GtmMigration").then((m) => ({
    default: m.GtmMigration,
  })),
);
const PrototypeLab = lazy(() => import("./admin/PrototypeLab/PrototypeLab"));
const DestinationsHealthOverviewPage = lazy(
  () => import("./health/DestinationsHealthOverviewPage"),
);
const CrossDomainTrackingPage = lazy(
  () => import("./crossDomainTracking/CrossDomainTrackingSettingsPage"),
);
const EpicShareLinkSettingsPage = lazy(
  () => import("./epicShareLink/EpicShareLinkSettingsPage"),
);
const AdBlockSettingsPage = lazy(() => import("./adblock/AdBlockSettingsPage"));
const AiChatPage = lazy(() =>
  import("./ai-chat/AiChatPage").then((m) => ({ default: m.default })),
);
const AiChatSettingsPage = lazy(() =>
  import("./ai-chat/AiChatSettingsPage").then((m) => ({
    default: m.default,
  })),
);
const AiChatApprovalGate = lazy(() =>
  import("./ai-chat/AiChatApprovalGate").then((m) => ({
    default: m.default,
  })),
);
const SurveysPage = lazy(() =>
  import("./surveys/SurveysPage").then((m) => ({ default: m.default })),
);
const FunnelsPage = lazy(() => import("./funnels/FunnelsPage"));
const HeatmapsPage = lazy(() => import("./heatmaps/HeatmapsPage"));
const SessionReplayPage = lazy(
  () => import("./sessionReplay/SessionReplayPage"),
);
const MasqueradePage = lazy(() => import("./MasqueradePage/MasqueradePage"));
const AIVisibilityPage = lazy(
  () => import("./admin/AIVisibilityPage/AIVisibilityPage"),
);

// Profiles
const ProfilesListPage = lazy(() => import("./profiles/ProfilesListPage"));
const ProfileDetailPage = lazy(() => import("./profiles/ProfileDetailPage"));

// Audiences
const AudiencesOverviewPageWithNavigation = lazy(() =>
  import("./audiences").then((m) => ({
    default: m.AudiencesOverviewPageWithNavigation,
  })),
);
const ImportedAudienceDetailPageWithNavigation = lazy(() =>
  import("./audiences").then((m) => ({
    default: m.ImportedAudienceDetailPageWithNavigation,
  })),
);
const ImportedAudiencesListPageWithNavigation = lazy(() =>
  import("./audiences").then((m) => ({
    default: m.ImportedAudiencesListPageWithNavigation,
  })),
);
const SegmentDetailPageWithNavigation = lazy(() =>
  import("./audiences").then((m) => ({
    default: m.SegmentDetailPageWithNavigation,
  })),
);
const SegmentsListPageWithNavigation = lazy(() =>
  import("./audiences").then((m) => ({
    default: m.SegmentsListPageWithNavigation,
  })),
);
const CohortPageWithNavigation = lazy(() => import("./personas"));

// Reports
const CrossDeviceAttributionPage = lazy(
  () => import("./reports/crossDeviceAttribution"),
);

// Tracker monitoring (pulls in highcharts + quill)
const WebTrackerManagerPage = lazy(
  () => import("./trackers/WebTrackerManagerPage"),
);
const WebTrackerDetailsPage = lazy(
  () => import("./trackers/WebTrackerDetailsPage"),
);
const TrackerBlockingPage = lazy(
  () => import("./trackers/TrackerBlockingPage"),
);
const WebTrackerCatalogPage = lazy(
  () => import("./trackers/WebTrackerCatalogPage"),
);
const DomainAdminDashboard = lazy(
  () => import("./trackers/DomainAdminDashboard"),
);
const ReportAdminDashboard = lazy(
  () => import("./trackers/ReportAdminDashboard"),
);
const ScanMonitoring = lazy(() => import("./trackers/ScanMonitoring"));
const SiteScannerPage = lazy(() => import("./trackers/SiteScannerPage"));
const SetUpDomainPage = lazy(
  () => import("./trackers/WebTrackerManagerPage/SetUpDomainPage"),
);
const AdminQueryWTMScansByTrackerNamesPage = lazy(
  () => import("./trackers/AdminQueryWTMScansByTrackerNamesPage"),
);

// Consent management
const ConsentManagementConfigPage = lazy(() =>
  import("./consent-management/components/ConfigPage/ConfigPage").then((m) => ({
    default: m.ConsentManagementConfigPage,
  })),
);
const ConsentManagementListPage = lazy(
  () => import("./consent-management/components/ConsentManagementListPage"),
);

//Accessibility
const AccessibilityHubPage = lazy(
  () => import("./accessibility/AccessibilityHubPage"),
);
const AccessibilityScanDetailPage = lazy(
  () => import("./accessibility/AccessibilityScanDetailPage"),
);

// Other lazy-loaded pages
const AuditLogsPage = lazy(() => import("./AuditLogsPage"));
const BlackholeAllowListPage = lazy(() => import("./blackhole/allowlist"));
const FunctionsPageWithNavigation = lazy(() =>
  import("./functions/FunctionsPage").then((m) => ({
    default: m.FunctionsPageWithNavigation,
  })),
);
const SourceFunctionsPageWithNavigation = lazy(() =>
  import("./functions/SourceFunctionsPage").then((m) => ({
    default: m.SourceFunctionsPageWithNavigation,
  })),
);
const LibraryPage = lazy(() => import("./LibraryPage"));
const LibraryPageV2 = lazy(() => import("./LibraryPageV2"));
const LiveViewPage = lazy(() => import("./LiveViewPage"));
const PHIAllowListPage = lazy(() => import("./PHIAllowListPage"));
const PlatformUsagePage = lazy(() => import("./PlatformUsagePage"));
const ProjectsPage = lazy(() => import("./projects"));
const SQLApp = lazy(() => import("./react-sql-editor/Containers/App"));
const SourcesPage = lazy(() => import("./sources/SourcesPage"));
const TeamPage = lazy(() => import("./TeamPage"));
const TransformationPage = lazy(
  () => import("./transforms/TransformationPage"),
);
const NewTransformationPage = lazy(
  () => import("./transforms/NewTransformationPage"),
);
const ExtractorPage = lazy(() =>
  import("./video/extractor/ExtractorPage").then((m) => ({
    default: m.ExtractorPage,
  })),
);
const VideoEditPage = lazy(() => import("./video/VideoEditPage"));
const VideoListPage = lazy(() => import("./video/VideoListPage"));
const VisualEditor = lazy(() => import("./VisualEditor"));

function NoMatch() {
  const accountMetadata = useAccountMetadata();
  if (!accountMetadata.loaded) {
    return (
      <div className="flex w-full items-center justify-center pt-100">
        <LoadingSpinner className="size-12" />
      </div>
    );
  }
  return <ErrorPage />;
}

export function LoginGatedPaths(props) {
  // TODO: we should be able to remove this redirect if we can confirm that
  // the contact-sales page automatically redirects to the thank you page
  // I was unable to do so as part of IND-1616 within a reasonable timeframe.
  // the form on contact-sales is a hubspot form that doesn't appear to have a
  // redirect configured so I'm unsure how this is happening.

  // http://freshpaint.io/contact-sales supposedly
  // redirects to https://app.freshpaint.io/?-signedup-&-thanks-
  // which we must then send to the actual thank you page
  if (
    window.location.search.includes("-signedup-") &&
    window.location.search.includes("-thanks-")
  ) {
    window.location.href = "https://freshpaint.io/contact-sales-thank-you-page";
  }

  if (!props.isSignedIn) {
    // prevent the next URL from sending user to sign out page
    const next = props.location.pathname.includes("signout")
      ? null
      : props.location.pathname + props.location.search;
    return <Redirect to={`/login?${queryString.stringify({ next })}`} />;
  } else {
    return <LoginGatedPathsHelper />;
  }
}

var snippetLoaded = false;

// loadFreshpaintSnippetOnce loads freshpaint.js. This function makes sure
// freshpaint.js is only loaded once.
function loadFreshpaintSnippetOnce(accountMetadata, callback = () => {}) {
  if (!snippetLoaded) {
    // prettier-ignore
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions, no-restricted-globals, no-cond-assign, eqeqeq, unused-imports/no-unused-vars
    (function (f, a) { if (!a.__SV) { var b = window; try { var d, m, j, k = b.location, c = k.hash; d = function (a, b) { return (m = a.match(RegExp(b + "=([^&]*)"))) ? m[1] : null }; c && d(c, "fpState") && (j = JSON.parse(decodeURIComponent(d(c, "fpState"))), "fpeditor" === j.action && (b.sessionStorage.setItem("_fpcehash", c), history.replaceState(j.desiredHash || "", f.title, k.pathname + k.search))) } catch (n) { } var l, h; window.freshpaint = a; a._i = []; a.init = function (b, d, g) { function c(b, i) { var a = i.split("."); 2 == a.length && (b = b[a[0]], i = a[1]); b[i] = function () { b.push([i].concat(Array.prototype.slice.call(arguments, 0))) } } var e = a; "undefined" !== typeof g ? e = a[g] = [] : g = "freshpaint"; e.people = e.people || []; e.toString = function (b) { var a = "freshpaint"; "freshpaint" !== g && (a += "." + g); b || (a += " (stub)"); return a }; e.people.toString = function () { return e.toString(1) + ".people (stub)" }; l = "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" "); for (h = 0; h < l.length; h++)c(e, l[h]); var f = "set set_once union unset remove delete".split(" "); e.get_group = function () { function a(c) { b[c] = function () { call2_args = arguments; call2 = [c].concat(Array.prototype.slice.call(call2_args, 0)); e.push([d, call2]) } } for (var b = {}, d = ["get_group"].concat(Array.prototype.slice.call(arguments, 0)), c = 0; c < f.length; c++)a(f[c]); return b }; a._i.push([b, d, g]) }; a.__SV = 1.3; b = f.createElement("script"); b.type = "text/javascript"; b.async = !0; b.src = "undefined" !== typeof FRESHPAINT_CUSTOM_LIB_URL ? FRESHPAINT_CUSTOM_LIB_URL : "//d1xv9akss4wau0.cloudfront.net/static/js/freshpaint.js"; d = f.getElementsByTagName("script")[0]; d.parentNode.insertBefore(b, d) } })(document, window.freshpaint || []);

    window.freshpaint.init("82ef97c4-8367-4d61-b0be-261498e9dd13", {
      api_host: "https://mc4by4yv08.execute-api.us-west-2.amazonaws.com/prod",
      app_host: "https://dw8zt32li9okr.cloudfront.net",
      integrations: {
        FullStory:
          !isAccountInHIPAAMode(accountMetadata) &&
          !inDev &&
          getCurrentUser() &&
          getCurrentUser()!.providerData[0].providerId !==
            GithubAuthProvider.PROVIDER_ID,
      },
    });

    callback();

    snippetLoaded = true;
  }
}

function EmployeeOnlyBadge() {
  const isMasquerading = Boolean(
    window.localStorage.getItem(MASQUERADE_LOCAL_STORAGE_KEY),
  );

  return (
    <span
      className={clsx(
        "fixed left-1/2 z-50 flex -translate-x-1/2 items-center justify-center gap-2 rounded-lg bg-blue-50 px-4 py-2 text-xs text-gray-500",
        isMasquerading ? "top-10" : "top-4",
      )}
    >
      FP EYES ONLY <EyeOffIcon size={16} />
    </span>
  );
}

function EmployeeOnlyRoute(routeProps: React.ComponentProps<typeof Route>) {
  if (!userIsFPEmployee()) {
    return (
      <Route
        path={routeProps.path}
        exact={routeProps.exact}
        strict={routeProps.strict}
        sensitive={routeProps.sensitive}
        render={() => <ErrorPage />}
      />
    );
  }

  return (
    <>
      <EmployeeOnlyBadge />
      <Route {...routeProps} />
    </>
  );
}

// LoginGatedPathsHelper is a subcomponent of LoginGatedPath. It has to be a
// separate component because we want to call hooks in LoginGatedPathsHelper
// that we won't always call in LoginGatedPaths. React requires that hooks called in a component are always called.
function LoginGatedPathsHelper() {
  const [currentMasquerade, _setCurrentMasquerade] = useState(
    window.localStorage.getItem(MASQUERADE_LOCAL_STORAGE_KEY),
  );

  const [user, setUser] = useState<AuthUser | null>(null);
  const accountMetadata = useAccountMetadata();
  const currentEnv = useCurrentEnvironment();
  const flag = useFeatureFlags();
  const dispatch = useDispatch();
  const users = useUsers();
  const { data: rolePermissions, isLoading: permsLoading } = usePermissions();

  useOnEnvironmentSet(() => {
    dispatch(fetchSources());
    dispatch(fetchFunctionDefinitions());
  });

  // When there's an incident, this will show a floating widget on the
  // screen
  useScriptTag("https://status.freshpaint.io/en/7715fa23/widget/script.js");

  useScrollToTopOnNavigationChange();

  useEffect(() => {
    if (accountMetadata && accountMetadata.projects.length > 0 && user) {
      loadFreshpaintSnippetOnce(accountMetadata, () => {
        window.freshpaint.identify(user?.email ?? undefined, {
          accountID: accountMetadata.accountID ?? undefined,
          hipaa: flag("hipaaV2"),
          email: user?.email ?? undefined,
        });
      });
      Sentry.setUser({ user: user.email, name: user.displayName });
    }
  }, [accountMetadata, flag, user]);

  // Force reloading the page when a user masquerades in another tab.
  useEffect(() => {
    window.addEventListener("storage", () => {
      const newValue = window.localStorage.getItem(
        MASQUERADE_LOCAL_STORAGE_KEY,
      );
      if (currentMasquerade !== newValue) {
        window.location.reload();
      }
    });
  });

  useEffect(() => {
    const unregisterPromise = onAuthStateChanged(async (user) => {
      if (
        inStaging &&
        user &&
        user.email &&
        !user.email.endsWith("@freshpaint.io")
      ) {
        toast.error(
          "This is the Freshpaint staging environment. If you're a Freshpaint employee, please sign in with your Freshpaint email address. If you're a customer, please sign in at https://app.freshpaint.io.",
        );
        signout();
        return;
      }

      // The execution order of different onAuthStateChanged observers is
      // finicky. This one may not run until after the parent's, so if the user
      // has just signed out, we may be unmounted. In order to avoid react
      // warnings about "Can't perform a React state update on an unmounted
      // component", we just make sure that the user is signed in before we
      // update it. This also avoids errors from making backend API calls when
      // we're not authenticated.
      if (user) {
        setUser(user);
        dispatch(fetchAccountMetadata());
        dispatch(fetchUsers());
      }
    });

    return () => {
      // Destroy freshchat widget on logout
      if (window.fcWidget) {
        (window.fcWidget as any).destroy();
      }
      // Make sure we un-register Firebase observers when the component unmounts.
      unregisterPromise.then((unregister) => unregister());
    };
  }, [dispatch]);

  const allSaasDestinations = useAllSaasDestinations();

  const history = useHistory();
  const { search } = useLocation();
  const queryParams = queryString.parse(search);
  const defaultTitle = "Freshpaint";

  useEffect(() => {
    const targetEnvID = queryParams.envId;
    if (typeof targetEnvID !== "string") {
      replaceHistoryStateToSamePageWithoutQueryParams(["envId"]);
      return;
    }

    if (targetEnvID === currentEnv?.ID) {
      replaceHistoryStateToSamePageWithoutQueryParams(["envId"]);
      return;
    }

    if (!accountMetadata.loaded) {
      return;
    }

    const targetEnv = (() => {
      for (const project of accountMetadata.projects) {
        for (const env of project.environments) {
          if (env.ID === targetEnvID) {
            return env;
          }
        }
      }

      return null;
    })();

    if (targetEnv !== null) {
      dispatch(setCurrentEnvironment(targetEnv.ID ?? "", queryClient));
    }

    replaceHistoryStateToSamePageWithoutQueryParams(["envId"]);
  }, [accountMetadata, currentEnv, dispatch, history, queryParams]);

  const wtmURLs = useWTMURLs();

  const canViewDestinationRanks = useCanViewDestinationRanks();

  const canAccessCRO = useCanAccessCRO();
  const canAccessAiChat = useCanAccessAiChat();

  if (accountMetadata.loaded && accountMetadata.projects.length === 0) {
    return <AppNoEnvironments />;
  }

  return (
    <AuthenticatedLayout>
      <FreshChat />
      <RequiredAccountInfoCollectionModal />
      <SurveyStoreProvider>
        <Suspense
          fallback={
            <div className="flex w-full items-center justify-center pt-100">
              <LoadingSpinner className="size-12" />
            </div>
          }
        >
          <Switch>
            <Route path="/" exact={true} component={EntryPage} />
            {!users.loading &&
              new FreshpaintUser(users.currentUser).hasAdminAccess() && (
                <Route
                  path="/system-status"
                  exact={true}
                  render={() => (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="System Status"
                    >
                      <SystemStatusPage />
                    </PageWithTitle>
                  )}
                />
              )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/dashboard"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics"
                    >
                      <AnalyticsDashboardPage />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/campaigns"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics: Campaigns"
                    >
                      <CampaignsListPage isCreatingNewCampaign={false} />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/campaigns/new"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics: Campaigns"
                    >
                      <CampaignsListPage isCreatingNewCampaign={true} />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/campaigns/:campaignID"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics: Campaign"
                    >
                      <AnalyticsDashboardPage />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/campaigns/edit/:campaignID"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics: Campaigns"
                    >
                      <CampaignsListPage isCreatingNewCampaign={false} />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/service-lines"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics: Service Lines"
                    >
                      <ServiceLinesListPage isCreatingNewServiceLine={false} />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/service-lines/new"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics: Service Lines"
                    >
                      <ServiceLinesListPage isCreatingNewServiceLine={true} />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/service-lines/:serviceLineID"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics: Service Line"
                    >
                      <AnalyticsDashboardPage />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics/service-lines/edit/:serviceLineID"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Analytics: Service Lines"
                    >
                      <ServiceLinesListPage isCreatingNewServiceLine={false} />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("analytics") && (
              <Route
                exact={true}
                path="/analytics"
                render={() => {
                  return <Redirect to="/analytics/dashboard" />;
                }}
              />
            )}
            {flag("marketingPerformanceAnalysis") && (
              <Route
                exact={true}
                path="/marketing-performance-analysis"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Ad Performance"
                    >
                      <MarketingPerformanceAnalysisPage />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("marketingPerformanceAnalysis") && (
              <Route
                exact={true}
                path="/marketing-performance-analysis/:campaignID"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Ad Performance"
                    >
                      <MarketingPerformanceAnalysisDetailPage />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("marketingPerformanceAnalysis") && (
              <Route
                exact={true}
                path="/marketing-performance-analysis/:campaignID/devices/:deviceID"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Ad Performance"
                    >
                      <MarketingPerformanceAnalysisDetailPage />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            {flag("marketingPerformanceAnalysis") && (
              <Route
                exact={true}
                path="/marketing-performance-analysis/:campaignID/devices/:deviceID/events/:eventID"
                render={() => {
                  return (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Ad Performance"
                    >
                      <MarketingPerformanceAnalysisDetailPage />
                    </PageWithTitle>
                  );
                }}
              />
            )}
            <Route
              exact={true}
              path="/events"
              render={() => {
                return <Redirect to={{ pathname: "/events/library" }} />;
              }}
            />
            {flag("eventLibraryV2") ? (
              <Route
                path="/events/library/:eventDefID?"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Event Library"
                  >
                    <LibraryPageV2 />
                  </PageWithTitle>
                )}
              />
            ) : (
              <Route
                path="/events/library/:eventDefID?"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Event Library"
                  >
                    <LibraryPage />
                  </PageWithTitle>
                )}
              />
            )}
            <Route
              path="/sources"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Setup">
                  <SourcesPage />
                </PageWithTitle>
              )}
            />
            {flag("sourceFunctions") && (
              <Route
                path="/source-functions"
                exact={true}
                component={SourceFunctionsPageWithNavigation}
              />
            )}
            {flag("sourceFunctions") && (
              <Route
                path="/source-functions/:sourceFunctionId"
                exact={true}
                component={SourceFunctionsPageWithNavigation}
              />
            )}
            <EmployeeOnlyRoute
              path="/masquerade"
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Masquerade">
                  <MasqueradePage />
                </PageWithTitle>
              )}
            />
            <EmployeeOnlyRoute
              exact={true}
              path="/admin/ai-visibility"
              render={() => <AIVisibilityPage />}
            />
            {canAccessAiChat && (
              <Route
                path="/ai-chat/settings"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="AI Chat Settings"
                  >
                    <AiChatApprovalGate>
                      <AiChatSettingsPage />
                    </AiChatApprovalGate>
                  </PageWithTitle>
                )}
              />
            )}
            {canAccessAiChat && (
              <Route
                path="/ai-chat"
                exact={true}
                render={() => (
                  <PageWithTitle defaultTitle={defaultTitle} title="AI Chat">
                    <AiChatApprovalGate>
                      <AiChatPage />
                    </AiChatApprovalGate>
                  </PageWithTitle>
                )}
              />
            )}
            {canAccessAiChat && (
              <Route
                path="/ai-chat/:conversationID"
                exact={true}
                render={() => (
                  <PageWithTitle defaultTitle={defaultTitle} title="AI Chat">
                    <AiChatApprovalGate>
                      <AiChatPage />
                    </AiChatApprovalGate>
                  </PageWithTitle>
                )}
              />
            )}
            {canAccessCRO && (
              <Route path="/surveys" render={() => <SurveysPage />} />
            )}
            {canAccessCRO && (
              <Route
                path="/funnels/:id"
                exact={true}
                render={(routeProps) => (
                  <PageWithTitle defaultTitle={defaultTitle} title="Funnels">
                    <FunnelsPage funnelId={routeProps.match.params.id} />
                  </PageWithTitle>
                )}
              />
            )}
            {canAccessCRO && (
              <Route
                path="/funnels"
                exact={true}
                render={() => (
                  <PageWithTitle defaultTitle={defaultTitle} title="Funnels">
                    <FunnelsPage />
                  </PageWithTitle>
                )}
              />
            )}
            {canAccessCRO && (
              <Route
                path="/web-analytics"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Web Analytics"
                  >
                    <AnalyticsDashboardPage />
                  </PageWithTitle>
                )}
              />
            )}
            {
              <Route
                path="/heatmaps"
                exact={true}
                render={() => (
                  <PageWithTitle defaultTitle={defaultTitle} title="Heatmaps">
                    <HeatmapsPage />
                  </PageWithTitle>
                )}
              />
            }
            {
              <Route
                path="/session-replay"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Session Replay"
                  >
                    <SessionReplayPage />
                  </PageWithTitle>
                )}
              />
            }
            {!users.loading &&
              !permsLoading &&
              allowAuditSelfServe(flag, users.currentUser, rolePermissions) && (
                <Route
                  path={AUDIT_LOGS_PATH}
                  exact={true}
                  render={() => (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Audit Logs"
                    >
                      <AuditLogsPage />
                    </PageWithTitle>
                  )}
                />
              )}
            {canViewDestinationRanks.canViewDestinationRanks && (
              <EmployeeOnlyRoute
                path="/admin/destination-ranks"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destination Ranks"
                  >
                    <DestinationRanksPage />
                  </PageWithTitle>
                )}
              />
            )}
            <EmployeeOnlyRoute
              path="/admin/accounts"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Accounts">
                  <AccountsPage />
                </PageWithTitle>
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/prototypes"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Prototype Lab"
                >
                  <PrototypeLab />
                </PageWithTitle>
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/accounts/:accountID"
              exact={true}
              render={() => <AccountMetricsRedirectPage />}
            />
            <EmployeeOnlyRoute
              path="/admin/accounts/:accountID/metrics"
              exact={true}
              render={() => (
                <AccountPage defaultTitle={defaultTitle} tab="metrics" />
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/accounts/:accountID/connectedEvents"
              exact={true}
              render={() => (
                <AccountPage
                  defaultTitle={defaultTitle}
                  tab="connectedEvents"
                />
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/accounts/:accountID/destinations"
              exact={true}
              render={() => (
                <AccountPage defaultTitle={defaultTitle} tab="destinations" />
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/accounts/:accountID/environments"
              exact={true}
              render={() => (
                <AccountPage defaultTitle={defaultTitle} tab="environments" />
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/environments/:environmentID"
              exact={true}
              render={() => <EnvironmentMetricsRedirectPage />}
            />
            <EmployeeOnlyRoute
              path="/admin/environments/:environmentID/metrics"
              exact={true}
              render={() => (
                <EnvironmentPage defaultTitle={defaultTitle} tab="metrics" />
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/environments/:environmentID/connectedEvents"
              exact={true}
              render={() => (
                <EnvironmentPage
                  defaultTitle={defaultTitle}
                  tab="connectedEvents"
                />
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/environments/:environmentID/destinations"
              exact={true}
              render={() => (
                <EnvironmentPage
                  defaultTitle={defaultTitle}
                  tab="destinations"
                />
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/destinations/:destinationID"
              exact={true}
              render={() => <DestinationPage defaultTitle={defaultTitle} />}
            />
            <EmployeeOnlyRoute
              path="/admin/events/:eventDefID"
              exact={true}
              render={() => <EventPage defaultTitle={defaultTitle} />}
            />
            {/* Redirect old /product/* URLs to /admin/* for backwards compatibility */}
            <Route
              path="/product/:path*"
              render={({ location, match }) => {
                const queryParams =
                  location.search !== "" ? `${location.search}` : "";

                return (
                  <Redirect
                    to={`/admin/${match.params.path || ""}${queryParams}`}
                  />
                );
              }}
            />
            <Route
              path="/settings/team"
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Account">
                  <TeamPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/settings/projects"
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Account">
                  <ProjectsPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/settings/platform-usage"
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Account">
                  <PlatformUsagePage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/events/editor"
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Visual Tagger"
                >
                  <VisualEditor />
                </PageWithTitle>
              )}
            />
            <Route
              path="/events/liveview"
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Live View">
                  <LiveViewPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/hipaa/allowlist"
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="HIPAA Allow List"
                >
                  <PHIAllowListPage />
                </PageWithTitle>
              )}
            />
            {accountMetadata.config.networkEventDefs && (
              <Route
                path={`${ALLOWLIST_PAGE_BASE_PATH}/:ruleID?`}
                render={() => (
                  <PageWithTitle defaultTitle={defaultTitle} title="Allowlist">
                    <BlackholeAllowListPage />
                  </PageWithTitle>
                )}
              />
            )}

            {/* WEB TRACKER MONITORING */}
            {flag("webTrackerMonitoring") && (
              <Route
                path={wtmURLs.trackersOverview}
                exact={true}
                render={() => (
                  <PageWithTitle defaultTitle={defaultTitle} title="Trackers">
                    <WebTrackerManagerPage tabValue={WTMTab.TrackerList} />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("webTrackerMonitoring") && (
              <Route
                path={wtmURLs.trackerDetailTpl}
                exact={true}
                render={() => (
                  <PageWithTitle defaultTitle={defaultTitle} title="Trackers">
                    <WebTrackerDetailsPage />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("webTrackerMonitoring") && (
              <Route
                path={wtmURLs.historicalScans}
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Historical Scans"
                  >
                    <WebTrackerManagerPage tabValue={WTMTab.HistoricalScans} />
                  </PageWithTitle>
                )}
              />
            )}

            {flag("webTrackerMonitoring") &&
              flag("webTrackerMonitoringTrackerBlockedUI") && (
                <Route
                  path={wtmURLs.trackerBlocking}
                  exact={true}
                  render={() => (
                    <PageWithTitle defaultTitle={defaultTitle} title="Trackers">
                      <TrackerBlockingPage />
                    </PageWithTitle>
                  )}
                />
              )}
            {flag("webTrackerMonitoring") && flag("unifiedDestinationsUI") && (
              <Route
                path={wtmURLs.phiAllowlist}
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="PHI Allowlist"
                  >
                    <WebTrackerManagerPage tabValue={WTMTab.TrackerList} />
                  </PageWithTitle>
                )}
              />
            )}

            {/* WEB TRACKER MONITORING LEGACY ROUTES SO WE DON'T BREAK PEOPLE'S BOOKMARKS */}
            {flag("webTrackerMonitoring") && (
              <Route
                path="/trackers"
                exact={true}
                render={() => <Redirect to="/monitoring/trackingTools" />}
              />
            )}
            {flag("webTrackerMonitoring") && (
              <Route
                path="/trackers/:trackerId"
                exact={true}
                render={() => <WebTrackerDetailsPageRedirect />}
              />
            )}
            {flag("webTrackerMonitoring") &&
              flag("webTrackerMonitoringTrackerBlockedUI") && (
                <Route
                  path="/trackers/trackerBlocking"
                  exact={true}
                  render={() => <Redirect to="/monitoring/trackerBlocking" />}
                />
              )}

            {/* DESTINATIONS V2 (Destination Monitoring) */}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/overview"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Overview"
                  >
                    <DashboardPage type="overview" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/demand-side-platforms"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Demand Side Platforms"
                  >
                    <DashboardPage type="DSPs" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/direct-response-ads"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Direct Response Ads"
                  >
                    <DashboardPage type="directResponseAds" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/audio"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Audio"
                  >
                    <DashboardPage type="audio" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/data-activation"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Data Activation"
                  >
                    <DashboardPage type="dataActivation" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && flag("audiences") && (
              <Route
                exact={true}
                path="/destinations/audiences"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Audiences"
                  >
                    <DashboardPage type="audiences" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/warehouses"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Data Warehouses"
                  >
                    <DashboardPage type="dataWarehouse" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/ctv"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: CTV"
                  >
                    <DashboardPage type="ctv" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/mmp"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Mobile Measurement"
                  >
                    <DashboardPage type="mmp" />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("destinationsV2") && (
              <Route
                exact={true}
                path="/destinations/catalog"
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Integrations: Catalog"
                  >
                    <CatalogPage />
                  </PageWithTitle>
                )}
              />
            )}

            {/* WEB TRACKER MONITORING ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/trackers/catalog"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Tracker Catalog"
                >
                  <WebTrackerCatalogPage />
                </PageWithTitle>
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/trackers/domainAdmin"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Domains">
                  <DomainAdminDashboard />
                </PageWithTitle>
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/trackers/scanMonitoring"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Manage Recurring Scans"
                >
                  <ScanMonitoring />
                </PageWithTitle>
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/trackers/scanMonitoring/:envID"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Manage Recurring Scans"
                >
                  <ScanMonitoring />
                </PageWithTitle>
              )}
            />
            <EmployeeOnlyRoute
              path="/admin/trackers/setUpDomain"
              exact={true}
              component={SetUpDomainPage}
            />
            <EmployeeOnlyRoute
              path="/admin/trackers/reportAdmin"
              exact={true}
              component={ReportAdminDashboard}
            />
            <EmployeeOnlyRoute
              path="/admin/trackers/reportsByTrackerDomain"
              exact={true}
              component={AdminQueryWTMScansByTrackerNamesPage}
            />
            <EmployeeOnlyRoute
              path="/admin/trackers/siteScanner"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Site Scanner">
                  <SiteScannerPage />
                </PageWithTitle>
              )}
            />

            {/* Customer MTU report ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/mtureports"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="MTU Reports">
                  <MtuReports />
                </PageWithTitle>
              )}
            />

            {/* Entitlement Control ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/entitlements"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Entitlements">
                  <Entitlements />
                </PageWithTitle>
              )}
            />

            {/* GTM Migration ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/gtmMigration"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="GTM Migration"
                >
                  <GtmMigration />
                </PageWithTitle>
              )}
            />

            {/* Cross Domain Tracking ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/crossdomaintracking"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Cross Domain Tracking Settings"
                >
                  <CrossDomainTrackingPage />
                </PageWithTitle>
              )}
            />

            {/* Epic Share Link ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/epicsharelink"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Epic Share Link Settings"
                >
                  <EpicShareLinkSettingsPage />
                </PageWithTitle>
              )}
            />

            {/* AdBlock Settings ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/adblock"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="AdBlock Settings"
                >
                  <AdBlockSettingsPage />
                </PageWithTitle>
              )}
            />

            {/* Ad Performance ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/adperformance"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Ad Performance"
                >
                  <AdPerformancePage />
                </PageWithTitle>
              )}
            />

            {/* Video Extractor ADMIN PAGES */}
            <EmployeeOnlyRoute
              path="/admin/videoextractor"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Video Extractor"
                >
                  <ExtractorPage />
                </PageWithTitle>
              )}
            />

            {/* Destinations Health Overview  */}
            <EmployeeOnlyRoute
              path="/admin/destination-health"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Destinations Health Overview"
                >
                  <DestinationsHealthOverviewPage />
                </PageWithTitle>
              )}
            />

            {/* TRANSFORMATIONS */}
            <Route
              path="/transformations/destination/:destination"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Transformations"
                >
                  <TransformationPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/transformations/event/:fpEventId"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Transformations"
                >
                  <TransformationPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/transformations/new/:intention"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Transformations"
                >
                  <NewTransformationPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/transformations/new"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Transformations"
                >
                  <NewTransformationPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/transformations/edit/:transformationSetId"
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Transformations"
                >
                  <TransformationPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/transformations"
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Transformations"
                >
                  <TransformationPage />
                </PageWithTitle>
              )}
            />

            {/* FORMS */}
            {
              <Route
                path="/forms"
                exact={true}
                render={() => <FormListPage />}
              />
            }
            {
              <Route
                path="/forms/builder/:formId?"
                render={(routeProps) => (
                  <FormBuilderPage
                    formId={routeProps.match.params?.formId ?? ""}
                  />
                )}
              />
            }
            {
              <Route
                path="/forms/submissions/:formId?"
                render={(routeProps) => (
                  <FormSubmissionListPage
                    formId={routeProps.match.params?.formId ?? ""}
                  />
                )}
              />
            }

            {
              <Route
                path="/forms/:formId/submissions/:submissionId"
                render={(routeProps) => (
                  <FormSubmissionView
                    formId={routeProps.match.params.formId}
                    submissionId={routeProps.match.params.submissionId}
                  />
                )}
              />
            }

            {
              <Route
                path="/forms/subscriptions/:formId"
                render={(routeProps) => (
                  <FormSubscriptionList
                    formId={routeProps.match.params.formId}
                  />
                )}
              />
            }

            {/* VIDEO */}
            {
              <Route
                path="/video"
                exact={true}
                render={() => <VideoListPage defaultTitle={defaultTitle} />}
              />
            }
            {
              <Route
                path="/video/:videoId"
                exact={true}
                render={(routeProps) => (
                  <VideoEditPage
                    defaultTitle={defaultTitle}
                    videoId={routeProps.match.params.videoId}
                  />
                )}
              />
            }

            {/* INVESTIGATE */}
            <Route
              path="/event-verification"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Event Verify">
                  <DestinationTraceListViewerWrapper
                    destinationSlug=""
                    destinationId=""
                  />
                </PageWithTitle>
              )}
            />
            {/* TODO CJ I believe this route is deprecated. */}
            <Route
              key="/event-verification/:traceId"
              path="/event-verification/:traceId"
              exact={true}
              render={(routeProps) => (
                <PageWithTitle defaultTitle={defaultTitle} title="Event Verify">
                  <DestinationTraceViewerWrapper
                    traceId={routeProps.match.params.traceId}
                    destinationSlug=""
                    destinationId=""
                  />
                </PageWithTitle>
              )}
            />

            {/* DESTINATIONS */}
            <Route
              path="/destinations"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                  <DestinationsListPage filter={["app", "advertising"]} />
                </PageWithTitle>
              )}
            />

            <Route
              path="/destinations/warehouses"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                  <WarehousesPage />
                </PageWithTitle>
              )}
            />
            <Route
              path="/destinations/apps"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Destinations">
                  <DestinationsListPage filter={["app", "advertising"]} />
                </PageWithTitle>
              )}
            />
            <Route
              path="/bing-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/bing-insights"
                      destinationName="bing-insights"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/facebook-pixel-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/facebook-pixel"
                      destinationName="facebook-pixel"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/google-ads-conversion-api-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/google-ads-conversion-api"
                      destinationName="google-ads-conversion-api"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/google-ads-insights-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/google-ads-insights"
                      destinationName="google-ads-insights"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/google-cm360-conversion-api-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/google-cm360-conversion-api"
                      destinationName="google-cm360-conversion-api"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/linkedin-ads-conversions-api-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/linkedin-ads-conversions-api"
                      destinationName="linkedin-ads-conversions-api"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/linkedin-audiences-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/linkedin-audiences"
                      destinationName="linkedin-audiences"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/linkedin-insights-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/linkedin-insights"
                      destinationName="linkedin-insights"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/reddit-ads-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/reddit-ads"
                      destinationName="reddit-ads"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/reddit-audiences-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/reddit-audiences"
                      destinationName="reddit-audiences"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/meta-audiences-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/meta-audiences"
                      destinationName="meta-audiences"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/tiktok-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/tiktok-insights"
                      destinationName="tiktok-insights"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/viant-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/viant-audiences"
                      destinationName="viant-audiences"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/microsoft-ads-audiences-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/microsoft-ads-audiences"
                      destinationName="microsoft-ads-audiences"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/meta-ad-performance-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/meta-insights"
                      destinationName="meta-ad-performance"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            <Route
              path="/google-ads-audiences-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationOAuth
                      destinationURL="/destinations/apps/google-ads-audiences"
                      destinationName="google-ads-audiences"
                    />
                  </PageWithTitle>
                ) : null
              }
            />
            {/* TODO CJ These routes should accept a destination id and pass that into the DestinationOAuth component */}
            <Route
              path="/github-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <DestinationOAuth destinationName="github" />
                ) : null
              }
            />
            <Route
              path="/google-tag-manager-oauth/"
              exact={true}
              render={() =>
                accountMetadata ? (
                  <DestinationOAuth
                    destinationURL="/admin/gtmMigration"
                    destinationName="google-tag-manager-migration"
                  />
                ) : null
              }
            />
            {/* This is the redirect url we use for sources for the popup. After a
        user lands here, the app will grab the token and close the window. */}
            <Route
              path="/oauth/"
              exact={true}
              render={() => {
                return <div />;
              }}
            />
            <Route
              path="/signout"
              render={() => (
                <PageWithTitle defaultTitle="Freshpaint" title="Sign out">
                  <SignoutPage />
                </PageWithTitle>
              )}
              exact
            />
            {(accountMetadata === null || flag("functions")) && (
              <Route
                path="/functions"
                exact={true}
                render={() =>
                  accountMetadata ? (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Functions"
                    >
                      <FunctionsPageWithNavigation />
                    </PageWithTitle>
                  ) : null
                }
              />
            )}
            {(accountMetadata === null || flag("functions")) && (
              <Route
                path="/functions/:functionDefId"
                exact={true}
                render={() =>
                  accountMetadata ? <FunctionsPageWithNavigation /> : null
                }
              />
            )}
            {(accountMetadata === null || flag("functions")) && (
              <Route
                path="/destinations/functions"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Destinations"
                  >
                    <DestinationsListPage filter={["function"]} />
                  </PageWithTitle>
                )}
              />
            )}
            <Route
              path="/consent-management/overview"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Consent Management"
                >
                  <ConsentManagementListPage />
                </PageWithTitle>
              )}
            />

            <Route
              path="/consent-management/metrics"
              exact={true}
              render={() => (
                <PageWithTitle
                  defaultTitle={defaultTitle}
                  title="Consent Metrics"
                >
                  <ConsentMetricsPage />
                </PageWithTitle>
              )}
            />

            {_.map(consentManagementIntegrations, (integration) => {
              const path = integration.configPageUrl();
              return (
                <Route
                  key={path}
                  path={path}
                  exact={true}
                  render={() => (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Consent Management"
                    >
                      <ConsentManagementConfigPage
                        consentManagementIntegrationSlug={integration.slug}
                      />
                    </PageWithTitle>
                  )}
                />
              );
            })}
            <Redirect
              from="/consent-management"
              to="/consent-management/overview"
            />
            {/* TODO(release): swap EmployeeOnlyRoute back to plain Route when
                releasing the accessibilityCompliance flag to customers. The
                backend authorize() in back/app/a11ycrawler/request_handler.go
                also has an FP-employee gate that needs to come out at the
                same time, otherwise customers will see the page but get 404s
                when they click "Run scan". */}
            {flag("accessibilityCompliance") && (
              <EmployeeOnlyRoute
                path="/accessibility"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Accessibility Compliance"
                  >
                    <AccessibilityHubPage />
                  </PageWithTitle>
                )}
              />
            )}
            {flag("accessibilityCompliance") && (
              <EmployeeOnlyRoute
                path="/accessibility/scans/:scanId"
                exact={true}
                render={() => (
                  <PageWithTitle
                    defaultTitle={defaultTitle}
                    title="Accessibility scan"
                  >
                    <AccessibilityScanDetailPage />
                  </PageWithTitle>
                )}
              />
            )}
            {(accountMetadata === null || flag("personas")) && (
              <Route
                path="/destinations/cohorting"
                exact={true}
                render={() =>
                  accountMetadata ? (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Destinations"
                    >
                      <DestinationsListPage filter={["cohorting"]} />
                    </PageWithTitle>
                  ) : null
                }
              />
            )}

            <Route
              path="/insights"
              exact
              render={() => (accountMetadata?.loaded ? <InsightsPage /> : null)}
            />

            {/* Profiles */}
            {flag("userProfiles") && (
              <EmployeeOnlyRoute
                path="/profiles"
                exact={true}
                render={() => <ProfilesListPage />}
              />
            )}
            {flag("userProfiles") && (
              <EmployeeOnlyRoute
                path="/profiles/:id"
                exact={true}
                render={() => <ProfileDetailPage />}
              />
            )}

            {/* Audiences */}
            <Route
              path="/audiences/imported"
              exact={true}
              render={() =>
                accountMetadata?.loaded ? (
                  flag("audiences") ? (
                    <ImportedAudiencesListPageWithNavigation
                      defaultTitle={defaultTitle}
                    />
                  ) : (
                    <Redirect to="/events/library" />
                  )
                ) : null
              }
            />
            <Route
              path="/audiences/imported/:id"
              exact={true}
              render={() =>
                accountMetadata?.loaded ? (
                  flag("audiences") ? (
                    <ImportedAudienceDetailPageWithNavigation
                      defaultTitle={defaultTitle}
                    />
                  ) : (
                    <Redirect to="/events/library" />
                  )
                ) : null
              }
            />

            <Route
              path="/audiences/segments"
              exact={true}
              render={() =>
                accountMetadata?.loaded ? (
                  flag("audiences") ? (
                    <>
                      <SegmentsListPageWithNavigation />
                    </>
                  ) : (
                    <Redirect to="/events/library" />
                  )
                ) : null
              }
            />

            <Route
              path="/audiences/segments/:id"
              exact={true}
              render={() =>
                accountMetadata?.loaded ? (
                  flag("audiences") ? (
                    <>
                      <SegmentDetailPageWithNavigation />
                    </>
                  ) : (
                    <Redirect to="/events/library" />
                  )
                ) : null
              }
            />

            <Route
              path="/audiences/overview"
              exact
              render={() =>
                accountMetadata?.loaded ? (
                  flag("audiences") ? (
                    <AudiencesOverviewPageWithNavigation />
                  ) : (
                    <Redirect to="/events/library" />
                  )
                ) : null
              }
            />

            {(accountMetadata === null || flag("personas")) && (
              <Route
                path="/cohorts"
                exact={true}
                render={() =>
                  accountMetadata ? (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Audiences"
                    >
                      <CohortPageWithNavigation />
                    </PageWithTitle>
                  ) : null
                }
              />
            )}
            {(accountMetadata === null || flag("personas")) && (
              <Route
                path="/cohort/"
                exact={true}
                render={() =>
                  accountMetadata ? (
                    <PageWithTitle defaultTitle={defaultTitle} title="Cohorts">
                      <CohortPageWithNavigation />
                    </PageWithTitle>
                  ) : null
                }
              />
            )}
            {(accountMetadata === null || flag("personas")) && (
              <Route
                path="/cohort/:cohortId"
                exact={true}
                render={() =>
                  accountMetadata ? (
                    <PageWithTitle defaultTitle={defaultTitle} title="Cohorts">
                      <CohortPageWithNavigation />
                    </PageWithTitle>
                  ) : null
                }
              />
            )}
            {(accountMetadata === null || flag("personas")) && (
              <Route
                path="/facebook-custom-audiences-oauth/"
                exact={true}
                render={() =>
                  accountMetadata ? (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Destinations"
                    >
                      <DestinationOAuth
                        destinationURL="/destinations/cohorting/facebook-custom-audiences"
                        destinationName="facebook-custom-audiences"
                      />
                    </PageWithTitle>
                  ) : null
                }
              />
            )}
            {(accountMetadata === null || flag("personas")) && (
              <Route
                path="/google-ads-remarketing-lists-oauth/"
                exact={true}
                render={() =>
                  accountMetadata ? (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Destinations"
                    >
                      <DestinationOAuth
                        destinationURL="/destinations/cohorting/google-ads-remarketing-lists"
                        destinationName="google-ads-remarketing-lists"
                      />
                    </PageWithTitle>
                  ) : null
                }
              />
            )}
            {(accountMetadata === null || flag("sqlTransformations")) && (
              <Route
                path="/sql-editor"
                exact={true}
                render={() => (
                  <PageWithTitle defaultTitle={defaultTitle} title="SQL Editor">
                    <SQLApp />
                  </PageWithTitle>
                )}
              />
            )}

            {_.flatMap(allSaasDestinations, (destination) => {
              var results: React.ReactNode[] = [];

              if (destination instanceof SaaSDestination) {
                const path = destination.configPageUrl();
                if (destination.supportsServerSide) {
                  const eventTesterPath = path + "/event-tester/:id";
                  results.push(
                    <Route
                      key={eventTesterPath}
                      path={eventTesterPath}
                      exact={true}
                      render={(routeProps) => (
                        <DestinationEventTester
                          destinationSlug={destination.slug}
                          destinationId={routeProps.match.params.id ?? ""}
                        />
                      )}
                    />,
                  );

                  const traceViewerPath = path + "/:id/event-verification";
                  results.push(
                    <Route
                      key={traceViewerPath}
                      path={traceViewerPath}
                      exact={true}
                      render={(routeProps) => (
                        <PageWithTitle
                          defaultTitle={defaultTitle}
                          title="Event Verify"
                        >
                          <DestinationTraceListViewerWrapper
                            destinationSlug={destination.slug}
                            destinationId={routeProps.match.params.id ?? ""}
                          />
                        </PageWithTitle>
                      )}
                    />,
                  );

                  const tracePath = path + "/:id/event-verification/:traceId";
                  results.push(
                    <Route
                      key={tracePath}
                      path={tracePath}
                      exact={true}
                      render={(routeProps) => (
                        <PageWithTitle
                          defaultTitle={defaultTitle}
                          title="Event Verify"
                        >
                          <DestinationTraceViewerWrapper
                            destinationSlug={destination.slug}
                            destinationId={routeProps.match.params.id ?? ""}
                            traceId={routeProps.match.params.traceId ?? ""}
                          />
                        </PageWithTitle>
                      )}
                    />,
                  );

                  const deliveryMetricsPath = path + "/delivery-metrics/:id";
                  results.push(
                    <Route
                      key={deliveryMetricsPath}
                      path={deliveryMetricsPath}
                      exact={true}
                      render={(routeProps) => (
                        <DestinationDeliveryMetrics
                          destinationSlug={destination.slug}
                          destinationId={routeProps.match.params.id}
                        />
                      )}
                    />,
                  );
                }

                if (
                  isAccountInHIPAAModeV2(accountMetadata) &&
                  destination.supportsServerSide
                ) {
                  const allowlistPath = path + "/phiallowlist/:id";
                  results.push(
                    <Route
                      key={allowlistPath}
                      path={allowlistPath}
                      exact={true}
                      render={(routeProps) => (
                        <DestinationPHIAllowListPage
                          destinationSlug={destination.slug}
                          destinationId={routeProps.match.params.id ?? ""}
                        />
                      )}
                    />,
                  );
                }

                results.push(
                  <Route
                    key={path}
                    path={path + "/:configId?"}
                    exact={false}
                    render={(routeProps) => (
                      <PageWithTitle
                        defaultTitle={defaultTitle}
                        title="Destinations"
                      >
                        <DestinationConfigPage
                          destinationSlug={destination.slug}
                          configId={routeProps.match.params?.configId}
                        />
                      </PageWithTitle>
                    )}
                  />,
                );
              }

              return results;
            })}

            {(accountMetadata === null || flag("cdaDashboard")) && (
              <Route
                path="/reports/cross-device-attribution"
                exact={true}
                render={() =>
                  accountMetadata ? (
                    <PageWithTitle
                      defaultTitle={defaultTitle}
                      title="Cross Device Attribution"
                    >
                      <CrossDeviceAttributionPage />
                    </PageWithTitle>
                  ) : null
                }
              />
            )}

            {(accountMetadata === null ||
              (flag("savedMoneyDashboard") && userIsFPEmployee())) && (
              <EmployeeOnlyRoute
                path="/money-saved"
                exact={true}
                render={() => (accountMetadata ? <MoneySavedPage /> : null)}
              />
            )}

            <Route
              path="/community"
              exact={true}
              render={() => (
                <PageWithTitle defaultTitle={defaultTitle} title="Community">
                  <CommunityPage />
                </PageWithTitle>
              )}
            />

            <Route component={NoMatch} />
          </Switch>
        </Suspense>
      </SurveyStoreProvider>
    </AuthenticatedLayout>
  );
}

type AppProps = {
  alert: AlertManager;
};

class App extends Component<
  AppProps,
  { isSignedIn?: boolean; token: string | null }
> {
  constructor(props) {
    super(props);

    initializeFirebase();

    this.state = {
      token: null,
    };
  }

  render() {
    const hostname = window && window.location && window.location.hostname;
    if (hostname === "perfalytics.com") {
      window.location.href = "https://freshpaint.io";
      return null;
    }

    if (this.state.isSignedIn === undefined) {
      return null;
    }

    return (
      <DarkModeProvider enabled={userIsFPEmployee()}>
        <MuiPickersUtilsProvider utils={CustomDateFnsUtils}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
              <BrowserRouter>
                <QueryParamProvider adapter={ReactRouter5Adapter}>
                  <BacknavLocationsProvider>
                    <Commandbar />

                    <Switch>
                      <Route
                        path="/login/sso/:providerName"
                        render={(props) => (
                          <SignInScreen
                            {...props}
                            isSignedIn={Boolean(this.state.isSignedIn)}
                            ssoOnly={true}
                          />
                        )}
                      />
                      <Route
                        path="/login"
                        render={(props) => (
                          <SignInScreen
                            {...props}
                            isSignedIn={Boolean(this.state.isSignedIn)}
                          />
                        )}
                      />
                      <Route
                        path="/invite"
                        render={(props) => (
                          <AcceptInvitePage
                            {...props}
                            isSignedIn={this.state.isSignedIn}
                          />
                        )}
                      />
                      <Route
                        path="/pending-invite"
                        exact={true}
                        render={(props) => (
                          <PendingInvitePage
                            {...props}
                            isSignedIn={this.state.isSignedIn}
                          />
                        )}
                      />
                      <Route
                        render={(props) => (
                          <LoginGatedPaths
                            {...props}
                            isSignedIn={this.state.isSignedIn}
                          />
                        )}
                      />
                    </Switch>
                  </BacknavLocationsProvider>
                </QueryParamProvider>
              </BrowserRouter>
              <DelightedNPS />
            </ThemeProvider>
          </QueryClientProvider>
        </MuiPickersUtilsProvider>
      </DarkModeProvider>
    );
  }

  private unregisterAuthObserver?: () => void;

  async componentDidMount() {
    this.unregisterAuthObserver = await onAuthStateChanged(async (user) => {
      SessionTimeoutManager.onAuthStateChanged(user !== null);

      const isSignedIn = !!user;
      this.setState({ isSignedIn });

      const checkForPendingInvite = async () => {
        if (
          window.location.pathname !== "/invite" &&
          window.location.pathname !== "/pending-invite"
        )
          try {
            const resp = await makeRequest<{ id: string }>(
              "get_pending_invite",
            );

            if (resp.data.id) {
              window.location.pathname = "/pending-invite";
            }
          } catch (error) {
            console.error(
              "Unable to connect to backend to check if pending invite",
              error,
            );
            Sentry.captureException(error);
          }
      };

      if (isSignedIn) {
        // Check for pending invites after the user fills out the HubSpot
        // form. This is to prevent the user from going back and forth
        // between the pending invite page and the hubspot page.
        await checkForPendingInvite();
      }
    });
  }

  // Make sure we un-register Firebase observers when the component unmounts.
  componentWillUnmount() {
    if (this.unregisterAuthObserver) {
      this.unregisterAuthObserver();
    }
  }
}

export default withAlert()(App);
